// Pure decision logic for the admin "Send questionnaire" modal, which doubles as
// a resend dialog. Extracted from the component so it can be unit-tested — same
// pattern as lib/companyPolicy.ts.

import { reviewableSubmissions, reviewTurn, type ReviewSubmission } from '@l2u/ui/lib/reviewState.ts';

export interface SendOption {
  id: string;
  name: string;
  assigned: boolean; // an assignment row already exists for this vendor
  sent_at: string | null; // ISO timestamp of the last (re)send, if assigned
  compliant?: boolean; // vendor is already approved on this questionnaire
}

// Verb + flag for the modal title and primary button. An already-assigned
// questionnaire is a resend; everything else is a first-time send.
export function describeSend(opt: SendOption | undefined): {
  verb: 'Send' | 'Resend';
  alreadySent: boolean;
} {
  if (opt?.assigned) return { verb: 'Resend', alreadySent: true };
  return { verb: 'Send', alreadySent: false };
}

// Dropdown label, e.g. "Insurance — sent 12 Jun" for an already-sent option, or
// "Safety — compliant" for one the vendor is already approved on (and so cannot
// be resent).
export function optionLabel(opt: SendOption, fmt: (iso: string) => string): string {
  if (opt.compliant) return `${opt.name} — compliant`;
  if (opt.assigned && opt.sent_at) return `${opt.name} — sent ${fmt(opt.sent_at)}`;
  return opt.name;
}

// A manual questionnaire is sendable only while active. An inactive one is hidden
// from the vendor portal (applicableQuestionnaireIds filters is_active=true), so
// offering it would email the vendor about something they can never see. Keyed on
// is_active, NOT `applicable` — manual mode intentionally bypasses contractor-type
// matching, which `applicable` folds in.
export function isManualSendable(q: { send_mode: string; is_active: boolean }): boolean {
  return q.send_mode === 'manual' && q.is_active;
}

/**
 * Whether the vendor is already approved on this questionnaire — the `compliant`
 * flag on a SendOption, which blocks a resend.
 *
 * Compliance-exempt and hidden questions are dropped by reviewableSubmissions.
 * Those are never reviewed, so their submissions stay 'pending' forever, and
 * counting them meant an approved questionnaire never registered as compliant
 * and stayed re-sendable. Shares reviewableSubmissions with the
 * per-questionnaire pill so the dropdown and the pill cannot disagree.
 */
export function isQuestionnaireCompliant<S extends ReviewSubmission>(
  questions: { id: string; exclude_from_compliance: boolean }[],
  subByQ: ReadonlyMap<string, S>,
  visibleIds: ReadonlySet<string>,
): boolean {
  return reviewTurn(reviewableSubmissions(questions, subByQ, visibleIds)) === 'compliant';
}

// A compliant questionnaire must not be (re)sent — the vendor is already
// approved on it. Drives the disabled <option> in the send dropdown.
export function isResendBlocked(opt: SendOption): boolean {
  return opt.compliant === true;
}

// First option that may still be (re)sent — the dropdown's default selection.
// '' when every option is blocked (all compliant) or the list is empty.
export function firstSendableId(opts: SendOption[]): string {
  return opts.find((o) => !isResendBlocked(o))?.id ?? '';
}
